import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import BioAgeCard from "@/components/results/BioAgeCard";
import PersonalDataModal from "@/components/PersonalDataModal";
import { motion } from "framer-motion";
import { FileText, Activity, AlertTriangle, CheckCircle2 } from "lucide-react";
import { estimateBiologicalAge } from "@/lib/bioAge";
import { buildAnalysisSummary, placeholderSummary } from "@/lib/analysisSummary";
import { loadProfile } from "@/lib/userProfile";

function useCount(value, duration = 900) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return v;
}

export default function ExecutiveSummary({ result, parsed, aggregate, findings }) {
  const { t, lang } = useLang();
  const [profile, setProfile] = useState(() => loadProfile());
  const [profileOpen, setProfileOpen] = useState(false);

  const total = useCount(aggregate.total);
  const inRange = useCount(aggregate.normal);
  const altered = useCount(aggregate.altered);

  const bioEstimate = useMemo(() => {
    if (!profile?.age) return null;
    return estimateBiologicalAge({
      chronologicalAge: profile.age,
      biomarkers: parsed.biomarkers || [],
    });
  }, [profile, parsed.biomarkers]);

  const analysisSummary = useMemo(() => {
    const built = buildAnalysisSummary({ parsed, result, lang });
    if (built?.text) return built.text;
    return placeholderSummary(lang);
  }, [parsed, result, lang]);

  return (
    <>
      <section className="grain relative overflow-hidden bg-[color:var(--sentinel-tint)]/40 pb-12 pt-8 md:pb-20 md:pt-12">
        <div className="container-x">
          <div className="flex flex-wrap items-center gap-3" data-testid="summary-meta">
            <span className="inline-flex items-center gap-2 rounded-full border border-[color:var(--sentinel-accent)] bg-white px-3 py-1.5 text-xs font-semibold text-[color:var(--sentinel-darker)]">
              <FileText className="h-3.5 w-3.5" />
              {result.originalFileName || "report.pdf"}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600">
              {t.results.summary.labDetected}: <strong className="text-zinc-900">{result.labName || "—"}</strong>
            </span>
            {parsed.patient?.fullName && (
              <span className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600">
                {t.results.summary.patient}: <strong className="text-zinc-900">{parsed.patient.fullName}</strong>
              </span>
            )}
            {parsed.patient?.recordNumber && (
              <span className="hidden items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 sm:inline-flex">
                {t.results.summary.record}: <span className="font-mono">{parsed.patient.recordNumber}</span>
              </span>
            )}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-[1.3fr_1fr]">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="overflow-hidden rounded-[28px] border border-zinc-200 bg-white p-8 md:p-10"
              data-testid="summary-headline"
            >
              <span className="overline">{t.results.summary.overline}</span>
              <h1 className="mt-4 text-4xl text-zinc-900 md:text-[56px] md:leading-[1.04]">
                {t.results.summary.titleHello}
              </h1>
              <p className="mt-4 max-w-xl text-base text-zinc-600">
                {t.results.summary.titleSub}
              </p>

              <div className="mt-10 grid grid-cols-3 gap-4">
                <Stat
                  icon={Activity}
                  value={total}
                  label={t.results.summary.biomarkers}
                  tone="brand"
                  testId="stat-total"
                />
                <Stat
                  icon={CheckCircle2}
                  value={inRange}
                  suffix={`/${aggregate.total}`}
                  label={t.results.summary.inRange}
                  tone="ok"
                  testId="stat-in-range"
                />
                <Stat
                  icon={AlertTriangle}
                  value={altered}
                  label={t.results.summary.altered}
                  tone={altered > 0 ? "warn" : "muted"}
                  testId="stat-altered"
                />
              </div>

              <div className="mt-10">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400">
                  {t.results.summary.keyFindings}
                </p>
                <ul className="mt-4 space-y-3" data-testid="key-findings">
                  {findings.length === 0 && (
                    <li className="text-sm text-zinc-500">—</li>
                  )}
                  {findings.map((f, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.08 }}
                      className="flex items-start gap-3 rounded-2xl border border-zinc-200 bg-zinc-50/60 p-4"
                      data-testid={`key-finding-${i}`}
                    >
                      <span
                        className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                          f.tone === "alert" ? "bg-amber-500" : "bg-[color:var(--sentinel)]"
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <p className="truncate text-sm font-semibold text-zinc-900">
                            {f.title}
                          </p>
                          {f.value && (
                            <span className="font-mono text-xs text-zinc-500">
                              {f.value}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-zinc-500">
                          {f.description ||
                            (f.direction === "high"
                              ? t.results.biomarkers.high
                              : f.direction === "low"
                              ? t.results.biomarkers.low
                              : t.results.biomarkers.onlyAltered)}
                        </p>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <div className="flex flex-col gap-5">
              <BioAgeCard
                estimate={bioEstimate}
                profile={profile}
                onEdit={() => setProfileOpen(true)}
              />
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="rounded-[28px] border border-zinc-200 bg-white p-6"
                data-testid="analysis-summary"
              >
                <span className="overline">{t.summary.overline}</span>
                <p className="mt-4 text-sm leading-relaxed text-zinc-600">
                  {analysisSummary}
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <PersonalDataModal
        open={profileOpen}
        initial={profile}
        onClose={() => setProfileOpen(false)}
        onSaved={(p) => {
          setProfile(p);
          setProfileOpen(false);
        }}
      />
    </>
  );
}

function Stat({ icon: Icon, value, suffix, label, tone, testId }) {
  const toneCls =
    tone === "ok"
      ? "text-emerald-600 bg-emerald-50"
      : tone === "warn"
      ? "text-amber-600 bg-amber-50"
      : tone === "brand"
      ? "text-[color:var(--sentinel)] bg-[color:var(--sentinel-tint)]"
      : "text-zinc-500 bg-zinc-100";
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid={testId}>
      <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${toneCls}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-4 flex items-baseline gap-1 font-mono">
        <span className="text-3xl font-semibold tracking-tight text-zinc-900">{value}</span>
        {suffix && <span className="text-sm text-zinc-400">{suffix}</span>}
      </div>
      <p className="mt-1 text-xs text-zinc-500">{label}</p>
    </div>
  );
}
